import { Asset, AssetState } from '@/api/generatedApi';
import { BodyText } from '@/components/BodyText';
import { TitleText } from '@/components/TitleText';
import { useReprocessAsset } from '@/hooks/api/assets/useReprocessAssets';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useToast } from '@/hooks/useToast';
import { AssetPageObjectModel } from '@/poms/assetPageObjectModel';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { Button, Dialog, DialogActions, DialogContent, IconButton, IconButtonProps, Tooltip } from '@mui/material';
import { useState } from 'react';

export const ReprocessAssetButton = ({
  asset,
  disabled,
  size = 'small',
}: {
  asset: Asset;
  disabled?: boolean;
  size?: IconButtonProps['size'];
}) => {
  const { permissions } = useGetCurrentUser();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { showToast } = useToast();

  const { reprocessAsset } = useReprocessAsset({
    onSuccess: () => showToast({ message: 'Asset queued for reprocessing', type: 'success' }),
    onError: () => showToast({ message: 'Failed to reprocess asset', type: 'error' }),
  });

  if (!permissions.canReprocessAssets || asset.state === AssetState.Finished) {
    return;
  }

  const handleClick = () => {
    setIsDialogOpen(true);
  };

  const handleClose = () => {
    setIsDialogOpen(false);
  };

  const handleConfirm = () => {
    reprocessAsset(asset.id);
    setIsDialogOpen(false);
  };

  return (
    <>
      <Tooltip title='Reprocess asset'>
        <span>
          <IconButton
            onClick={handleClick}
            size={size}
            disabled={Boolean(disabled)}
            data-testid={AssetPageObjectModel.ReprocessAssetButton}
          >
            <AutorenewIcon sx={{ fontSize: '22px' }} />
          </IconButton>
        </span>
      </Tooltip>
      <Dialog
        fullWidth
        open={isDialogOpen}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '12px',
            },
          },
        }}
      >
        <TitleText sx={{ padding: '24px 8px 0 24px' }}>Reprocess Asset</TitleText>
        <DialogContent sx={{ padding: '8px 24px 24px 24px' }}>
          <BodyText color='text.primary'>
            This asset will be queued for reprocessing. Are you sure you want to continue?
          </BodyText>
        </DialogContent>
        <DialogActions sx={{ padding: '0 24px 24px 24px' }}>
          <Button onClick={handleClose} sx={{ textTransform: 'none', borderRadius: '12px', padding: '12px 16px' }}>
            <BodyText color='text.primary'>Cancel</BodyText>
          </Button>
          <Button
            variant='contained'
            onClick={handleConfirm}
            sx={{ textTransform: 'none', borderRadius: '12px', padding: '8px 16px' }}
          >
            Reprocess
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
